import * as echarts from 'echarts';

import usaGeoUrl from '../assets/geo/USA.json?url';
import worldGeoUrl from '../assets/geo/world.json?url';

/** Bundled-first; CDNs optional when corporate firewall blocks apache/jsDelivr-only paths. */
const FALLBACK_CHAIN = /** @type {const} */ ({
    world: [worldGeoUrl, 'https://cdn.jsdelivr.net/npm/echarts@4.9.0/map/json/world.json'],
    USA: [
        usaGeoUrl,
        'https://echarts.apache.org/examples/data/asset/geo/USA.json',
    ],
});

const MAP_REGISTER_AS = /** @type {const} */ ({
    world: 'world',
    USA: 'USA',
});

const loadPromises = /** @type {Record<string, Promise<void> | undefined>} */ ({});

function isMapRegistered(registerAs) {
    try {
        return !!(echarts.getMap && echarts.getMap(registerAs));
    } catch {
        return false;
    }
}

/**
 * @param {'world' | 'USA'} mapId
 */
async function fetchGeoJson(mapId) {
    const urls = FALLBACK_CHAIN[mapId];
    if (!urls?.length) {
        throw new Error(`Unknown geo map: ${mapId}`);
    }
    let lastErr;
    for (const url of urls) {
        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`${res.status}`);
            return res.json();
        } catch (e) {
            lastErr = e;
        }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

/**
 * Fetch + register once per map id. `mapId` is 'world' | 'USA' (same as series.map).
 * @param {'world' | 'USA'} mapId
 */
export function ensureMapRegistered(mapId) {
    const registerAs = MAP_REGISTER_AS[mapId];
    if (!registerAs) {
        return Promise.reject(new Error(`Unknown geo map: ${mapId}`));
    }
    if (isMapRegistered(registerAs)) {
        return Promise.resolve();
    }

    if (!loadPromises[mapId]) {
        loadPromises[mapId] = fetchGeoJson(mapId)
            .then((geoJson) => {
                echarts.registerMap(registerAs, geoJson);
            })
            .catch((err) => {
                delete loadPromises[mapId];
                throw err;
            });
    }

    return loadPromises[mapId];
}

/** @deprecated use ensureMapRegistered('world') */
export function ensureWorldMapRegistered() {
    return ensureMapRegistered('world');
}
